import { useQuoteHistory } from '@/shared/api';
import { formatBalance } from '@/shared/lib';
import { DeltaLabel, MarketSparkline } from '@/shared/components';
import { cn } from '@/shared/utils';
import { MARKET_CHART_PERIOD } from '../config/marketTabs';
import type { MarketAsset } from '../model';

const ROW_HOVER_CLASSNAME =
  'relative isolate before:absolute before:-inset-x-[0.4rem] before:inset-y-px before:-z-10 before:rounded-[1.6rem] before:bg-secondary-bg before:opacity-0 before:transition-opacity tablet:hover:before:opacity-100';

type Props = {
  asset: MarketAsset;
  baseAsset: string;
  withSparkline?: boolean;
  withHover?: boolean;
};

const RowSparkline = ({
  code,
  baseAsset,
  isPositive,
}: {
  code: string;
  baseAsset: string;
  isPositive: boolean;
}) => {
  const { points } = useQuoteHistory({
    asset: code,
    baseAsset,
    period: MARKET_CHART_PERIOD,
  });

  return <MarketSparkline points={points} isPositive={isPositive} />;
};

const MarketAssetRow = ({
  asset,
  baseAsset,
  withSparkline = true,
  withHover = true,
}: Props) => {
  return (
    <div
      className={cn(
        'flex h-[5.4rem] items-center gap-[1.6rem] px-[0.8rem]',
        withHover && ROW_HOVER_CLASSNAME
      )}>
      <div className="flex min-w-0 flex-1 items-center gap-[1.2rem]">
        {asset.icon ? (
          <img
            src={asset.icon}
            alt=""
            className="size-[2.8rem] shrink-0 rounded-full"
          />
        ) : (
          <span className="size-[2.8rem] shrink-0 rounded-full bg-secondary-bg" />
        )}
        <span className="headline5 text-primary shrink-0">{asset.code}</span>
        <span className="medium5 text-secondary truncate hidden tablet:inline">
          {asset.name}
        </span>
      </div>

      <span className="medium5 text-primary text-right whitespace-nowrap mr-[0.8rem] tablet:mr-[2.4rem]">
        {formatBalance(asset.price, asset.precision)} {baseAsset}
      </span>

      <div className="flex w-[6.4rem] shrink-0">
        <DeltaLabel
          deltaPercent={asset.deltaPercent}
          size="card"
          showLabelText={false}
          className="gap-0 pl-[0.4rem] pr-[0.8rem] py-[0.2rem]"
        />
      </div>

      {withSparkline && (
        <RowSparkline
          code={asset.code}
          baseAsset={baseAsset}
          isPositive={(asset.deltaPercent ?? 0) >= 0}
        />
      )}
    </div>
  );
};

export { MarketAssetRow };
